import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  InfiniteScrollMultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@features/ui';
import {
  createCouponBatch,
  updateCouponBatch,
  getCouponBatchById,
  createCouponCode,
} from '@src/services/coupons';
import { useTradingUserInfiniteOptions } from '@hooks/useTradingUserInfiniteOptions';
import { toast } from 'react-toastify';

type TForm = {
  usageCount: number;
  discountValue: number | '';
  discountType: 'percent' | 'number';
  comment: string;
  userCouponUsageCount: number;
  from?: string;
  to?: string;
  enableDateRange: boolean;
  isApplyRecurring: boolean;
  isDisabled: boolean;
  tradingUserIds: number[];
  excludeTradingUserIds: number[];
};

export const CouponEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userOptions, loading, hasMore, loadMore } =
    useTradingUserInfiniteOptions();

  const [form, setForm] = useState<TForm>({
    usageCount: 1,
    discountValue: '',
    discountType: 'percent',
    comment: '',
    userCouponUsageCount: 1,
    from: undefined,
    to: undefined,
    enableDateRange: false,
    isApplyRecurring: false,
    isDisabled: false,
    tradingUserIds: [],
    excludeTradingUserIds: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  useEffect(() => {
    if (id && id !== 'create') {
      // load coupon batch from API and populate form
      getCouponBatchById(Number(id)).then((res) => {
        const it = res.data;

        if (!it) return;
        setForm({
          usageCount: it.use_count ?? 1,
          discountValue: Number(it.discount),
          discountType: (it.discount_type as 'percent' | 'number') ?? 'percent',
          comment: it.comment ?? '',
          userCouponUsageCount: it.user_use_count ?? 1,
          from: it.start_date ? it.start_date.split('T')[0] : undefined,
          to: it.end_date ? it.end_date.split('T')[0] : undefined,
          enableDateRange: !!(it.start_date || it.end_date),
          isApplyRecurring: it.is_apply_recurring ?? false,
          isDisabled: it.is_disabled ?? false,
          tradingUserIds: it.trading_user_ids ?? [],
          excludeTradingUserIds: it.exclude_trading_user_ids ?? [],
        });
        // Open advanced settings if any advanced field has a value
        if (
          it.user_use_count ||
          it.start_date ||
          it.end_date ||
          it.is_apply_recurring ||
          it.is_disabled ||
          it.trading_user_ids?.length ||
          it.exclude_trading_user_ids?.length
        ) {
          setIsAdvancedOpen(true);
        }
      });
    }
  }, [id]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};

    if (form.discountValue === '' || Number(form.discountValue) <= 0)
      e.discountValue = 'Discount must be greater than 0';
    if (form.usageCount < 0) e.usageCount = 'Usage count must be >= 0';
    if (form.enableDateRange && form.from && form.to) {
      const from = new Date(form.from);
      const to = new Date(form.to);

      if (from > to) e.date = 'From date must be before To date';
    }
    setErrors(e);

    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev?: React.FormEvent) => {
    ev?.preventDefault();
    if (!validate()) return;
    // Map form to backend payload
    const payload = {
      discount: Number(form.discountValue),
      discount_type: form.discountType,
      use_count: form.usageCount > 0 ? form.usageCount : undefined,
      start_date:
        form.enableDateRange && form.from
          ? new Date(form.from).toISOString()
          : undefined,
      end_date:
        form.enableDateRange && form.to
          ? new Date(form.to).toISOString()
          : undefined,
      comment: form.comment || undefined,
      // Additional fields for advanced settings
      user_use_count:
        form.userCouponUsageCount > 0 ? form.userCouponUsageCount : undefined,
      is_apply_recurring: form.isApplyRecurring || undefined,
      is_disabled: form.isDisabled || undefined,
      trading_user_ids:
        form.tradingUserIds.length > 0 ? form.tradingUserIds : undefined,
      exclude_trading_user_ids:
        form.excludeTradingUserIds.length > 0
          ? form.excludeTradingUserIds
          : undefined,
    };

    if (id && id !== 'create') {
      // update
      updateCouponBatch(Number(id), payload)
        .then(() => navigate('/admin/coupons'))
        .catch(() => toast.error('Failed to update coupon batch.'));
    } else {
      createCouponBatch(payload)
        .then((res) => {
          const newBatch = res.data;

          return createCouponCode(newBatch.id, { count: 1 }).catch(() => {
            toast.warn(
              'Batch created, but default coupon code could not be generated.'
            );
          });
        })
        .then(() => navigate('/admin/coupons'))
        .catch(() => toast.error('Failed to create coupon batch.'));
    }
  };

  const setField = (k: keyof TForm, value: any) => {
    setForm((s) => ({ ...s, [k]: value }));
  };

  return (
    <div className="min-w-[700px] pb-8">
      <div className={'flex justify-between'}>
        <div className={'flex flex-col gap-y-2.5'}>
          <p className={'text-[23px] font-medium text-blue'}>
            {id && id !== 'create'
              ? `Edit Coupon Batch (#${id}${form.comment ? ` - ${form.comment}` : ''})`
              : 'Create Coupon Batch'}
          </p>
          <div className={'h-[2px] w-full bg-blue'} />
        </div>
      </div>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
        {/* Coupons Usage Count */}
        <div>
          <label
            className="block text-sm font-medium mb-1"
            htmlFor="usageCount"
          >
            Coupons Usage Count
          </label>
          <p className="text-sm text-gray-500 mb-2">
            how many times single coupon can be used
          </p>
          <input
            className="border border-gray-300 rounded px-3 py-2 w-full max-w-[200px]"
            id="usageCount"
            min="0"
            type="number"
            value={form.usageCount}
            onChange={(e) =>
              setField(
                'usageCount',
                e.target.value === '' ? 0 : Number(e.target.value)
              )
            }
          />
          {errors.usageCount && (
            <div className="text-red-600 text-sm mt-1">{errors.usageCount}</div>
          )}
        </div>

        {/* Discount */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            htmlFor="discountValue"
          >
            Discount
          </label>
          <div className="flex gap-2 max-w-[300px]">
            <input
              className="border border-gray-300 rounded px-3 py-2 flex-1"
              id="discountValue"
              step="0.01"
              type="number"
              value={form.discountValue as any}
              onChange={(e) =>
                setField(
                  'discountValue',
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
            />
            <Select
              value={form.discountType}
              onValueChange={(value) =>
                setField('discountType', value as 'percent' | 'number')
              }
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">%</SelectItem>
                <SelectItem value="number">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.discountValue && (
            <div className="text-red-600 text-sm mt-1">
              {errors.discountValue}
            </div>
          )}
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="comment">
            Comment
          </label>
          <p className="text-sm text-gray-500 mb-2">for admin reference</p>
          <textarea
            className="border border-gray-300 rounded px-3 py-2 w-full min-h-[80px]"
            id="comment"
            value={form.comment}
            onChange={(e) => setField('comment', e.target.value)}
          />
        </div>

        {/* Advanced Settings */}
        <div className="border-t pt-4">
          <button
            className="text-sm hover:cursor-pointer flex items-center gap-2"
            type="button"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          >
            <span>{isAdvancedOpen ? '−' : '+'}</span>
            <span>Advanced Settings</span>
          </button>

          {isAdvancedOpen && (
            <div className="mt-6 space-y-6">
              {/* User Coupon Usage Count */}
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="userCouponUsageCount"
                >
                  User Coupon Usage Count
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  how many times a coupon code can be used by customer
                </p>
                <input
                  className="border border-gray-300 rounded px-3 py-2 w-full max-w-[200px]"
                  id="userCouponUsageCount"
                  min="0"
                  type="number"
                  value={form.userCouponUsageCount}
                  onChange={(e) =>
                    setField(
                      'userCouponUsageCount',
                      e.target.value === '' ? 0 : Number(e.target.value)
                    )
                  }
                />
              </div>

              {/* Dates */}
              <div>
                <label className="block text-sm font-medium mb-1">Dates</label>
                <p className="text-sm text-gray-500 mb-3">
                  date range when coupon can be used
                </p>
                <div className="flex items-center gap-3 mb-3">
                  <Checkbox
                    checked={form.enableDateRange}
                    onCheckedChange={(checked) =>
                      setField('enableDateRange', checked === true)
                    }
                  />
                  <span className="text-sm">Enable date range</span>
                </div>
                {form.enableDateRange && (
                  <div className="flex gap-3 items-center">
                    <input
                      className="border border-gray-300 rounded px-3 py-2"
                      id="from"
                      type="date"
                      value={form.from ?? ''}
                      onChange={(e) =>
                        setField(
                          'from',
                          e.target.value ? e.target.value : undefined
                        )
                      }
                    />
                    <span className="text-sm">−</span>
                    <input
                      className="border border-gray-300 rounded px-3 py-2"
                      id="to"
                      type="date"
                      value={form.to ?? ''}
                      onChange={(e) =>
                        setField(
                          'to',
                          e.target.value ? e.target.value : undefined
                        )
                      }
                    />
                  </div>
                )}
                {errors.date && (
                  <div className="text-red-600 text-sm mt-2">{errors.date}</div>
                )}
              </div>

              {/* Apply to recurring? */}
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={form.isApplyRecurring}
                  onCheckedChange={(checked) =>
                    setField('isApplyRecurring', checked === true)
                  }
                />
                <div>
                  <label className="text-sm font-medium">
                    Apply to recurring?
                  </label>
                  <p className="text-sm text-gray-500">
                    apply coupon discount to recurring rebills?
                  </p>
                </div>
              </div>

              {/* Is Disabled? */}
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={form.isDisabled}
                  onCheckedChange={(checked) =>
                    setField('isDisabled', checked === true)
                  }
                />
                <div>
                  <label className="text-sm font-medium">Is Disabled?</label>
                  <p className="text-sm text-gray-500">
                    If you disable this coupons batch, it will not be available
                    for new purchases. Existing invoices are not affected.
                  </p>
                </div>
              </div>

              {/* Trading Users */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Trading Users
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  coupons can be used by selected trading users only. If nothing
                  selected, coupon can be used by any user
                </p>
                <InfiniteScrollMultiSelect
                  hasMore={hasMore}
                  isLoading={loading}
                  options={userOptions}
                  placeholder="− Please Select −"
                  value={form.tradingUserIds}
                  onChange={(value) => setField('tradingUserIds', value)}
                  onMenuScrollToBottom={loadMore}
                />
              </div>

              {/* Exclude Trading Users */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Exclude Trading Users
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  do not allow selected trading users to use coupons from this
                  batch
                </p>
                <InfiniteScrollMultiSelect
                  hasMore={hasMore}
                  isLoading={loading}
                  options={userOptions}
                  placeholder="− Please Select −"
                  value={form.excludeTradingUserIds}
                  onChange={(value) => setField('excludeTradingUserIds', value)}
                  onMenuScrollToBottom={loadMore}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-4">
          <Button size={'sm'} type="submit" variant={'lime'}>
            Save
          </Button>
          <Button
            size={'sm'}
            type="button"
            variant={'secondary'}
            onClick={() => navigate('/admin/coupons')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CouponEditPage;
